import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BookOpenIcon, PlayIcon } from 'lucide-react';
import { DataTable, EmptyState, PageHeading, Pagination, Panel, SearchInput, SelectInput, type DataTableSort } from '@bcecloud/portal-ui';
import { allDocs, helpAreas, isDocVisible, searchDocs, type HelpDoc } from '@/help/content';
import { useFeatureLookup } from '@/lib/app-features';
import { usePermissionLookup } from '@/lib/use-permission-lookup';
import { sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';

const SORT_FIELDS = { page: 'title', area: 'areaLabel', updated: 'updated' } as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.area, direction: 'asc' };
const HELP_PAGE_SIZE = 25;
/** The `?area=` value meaning every area. */
const ALL_AREAS = '';
const AREA_PARAM = 'area';

interface HelpRow {
  readonly doc: HelpDoc;
  readonly title: string;
  readonly areaLabel: string;
  readonly areaRank: number;
  readonly updated: string;
}

/**
 * Help: every feature documented, one row per page, searchable, and narrowed
 * to one area when the reader picks it (kept in `?area=`, so a link can open
 * Help on one area). The pages are Markdown in the repo, written with the
 * feature and shipped with it.
 */
export function HelpPage(): React.ReactElement {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);
  const query = search.trim();
  const areas = helpAreas();
  const areaLabel = new Map(areas.map((area) => [area.key, area.label]));
  const areaRank = new Map(areas.map((area, index) => [area.key, index]));
  const isFeatureOn = useFeatureLookup();
  const hasPermission = usePermissionLookup();
  const [params, setParams] = useSearchParams();
  const readable = allDocs().filter((doc) => isDocVisible(doc, isFeatureOn, hasPermission));
  // Only areas the reader has pages in; an area in the URL they cannot see reads as all.
  const readableAreas = areas.filter((area) => readable.some((doc) => doc.area === area.key));
  const requested = params.get(AREA_PARAM) ?? ALL_AREAS;
  const area = readableAreas.some((candidate) => candidate.key === requested) ? requested : ALL_AREAS;
  const docs = (query ? searchDocs(query) : allDocs())
    .filter((doc) => isDocVisible(doc, isFeatureOn, hasPermission))
    .filter((doc) => area === ALL_AREAS || doc.area === area);
  const rows = sortRows(
    docs.map((doc): HelpRow => ({ doc, title: doc.title, areaLabel: areaLabel.get(doc.area) ?? doc.area, areaRank: areaRank.get(doc.area) ?? 99, updated: doc.updated ?? '' })),
    sort,
  );
  const totalPages = Math.max(1, Math.ceil(rows.length / HELP_PAGE_SIZE));
  const visible = rows.slice((page - 1) * HELP_PAGE_SIZE, page * HELP_PAGE_SIZE);

  return (
    <section className="animate-portal-rise">
      <PageHeading eyebrow="HELP" title="Help" subtitle="What each part of the app does and how to work it." />
      <div className="mt-6 mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search help"
          placeholder="A feature, a screen, a word from the page"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          className="w-full max-w-md"
        />
        {readableAreas.length > 1 ? (
          <div className="w-full max-w-xs">
            <label htmlFor="help-area" className="sr-only">
              Area
            </label>
            <SelectInput
              id="help-area"
              value={area}
              onChange={(event) => {
                const next = new URLSearchParams(params);
                if (event.target.value === ALL_AREAS) next.delete(AREA_PARAM);
                else next.set(AREA_PARAM, event.target.value);
                setParams(next, { replace: true });
                setPage(1);
              }}
            >
              <option value={ALL_AREAS}>All areas</option>
              {readableAreas.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </SelectInput>
          </div>
        ) : null}
      </div>
      {allDocs().length === 0 ? (
        <Panel>
          <EmptyState icon={<BookOpenIcon aria-hidden className="size-6" />} title="No help pages yet" message="Pages are written in docs/help alongside each feature and appear here on the next deploy." />
        </Panel>
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState icon={<BookOpenIcon aria-hidden className="size-6" />} title="Nothing matches" message="Try a different word, or the name of the screen." />
        </Panel>
      ) : (
        <>
          <DataTable headers={[{ label: 'Page', sortKey: SORT_FIELDS.page }, { label: 'Area', sortKey: SORT_FIELDS.area }, { label: 'Updated', sortKey: SORT_FIELDS.updated }, 'Actions']} visuallyHiddenHeaders={[3]} sort={sort} onSort={onSort} minWidth={720}>
            {visible.map((row) => (
              <tr key={row.doc.path} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small">
                  <Link to={row.doc.path} className="font-bold text-portal-ink hover:text-brand">
                    {row.title}
                  </Link>
                  {row.doc.summary ? <div className="text-portal-micro text-portal-muted">{row.doc.summary}</div> : null}
                </td>
                <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{row.areaLabel}</td>
                <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{row.updated || '—'}</td>
                <td className="p-3 text-portal-small whitespace-nowrap">
                  <span className="flex items-center gap-3">
                    <Link to={row.doc.path} className="font-semibold text-brand hover:underline">
                      Read
                    </Link>
                    {row.doc.guide ? (
                      <Link to={`${row.doc.path}?guide=1`} className="flex items-center gap-1 font-semibold text-brand hover:underline">
                        <PlayIcon aria-hidden className="size-3" /> Take the tour
                      </Link>
                    ) : null}
                  </span>
                </td>
              </tr>
            ))}
          </DataTable>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} summary={`${rows.length} page${rows.length === 1 ? '' : 's'}`} />
        </>
      )}
    </section>
  );
}
